import React, { useState, useEffect } from "react";

export default function WebDevelopment() {
  const [data, setData] = useState({
    faqs: [],
    testimonials: [],
    services: [],
  });
  const [error, setError] = useState(null);
  const [currentTestimonialIndex, setCurrentTestimonialIndex] = useState(0);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [faqsRes, servicesRes, testimonialsRes] = await Promise.all([
          fetch("/Database/Webfaqs.json"),
          fetch("/Database/WebServices.json"),
          fetch("/Database/WebTestimonials.json"),
        ]);

        if (!faqsRes.ok || !servicesRes.ok || !testimonialsRes.ok) {
          throw new Error("Error fetching one or more resources");
        }

        const [faqs, services, testimonials] = await Promise.all([
          faqsRes.json(),
          servicesRes.json(),
          testimonialsRes.json(),
        ]);

        setData({
          faqs: Array.isArray(faqs) ? faqs : [],
          services: Array.isArray(services) ? services : [],
          testimonials: Array.isArray(testimonials) ? testimonials : [],
        });
      } catch (error) {
        console.error("Error fetching data:", error);
        setError("There was an error loading the data. Please try again later.");
      }
    };

    fetchData();
  }, []);

  // Automatic slider effect for testimonials
  useEffect(() => {
    const interval = setInterval(() => {
      setCurrentTestimonialIndex((prevIndex) =>
        prevIndex === data.testimonials.length - 1 ? 0 : prevIndex + 1
      );
    }, 5000); // Change every 5 seconds

    return () => clearInterval(interval); // Clean up interval on unmount
  }, [data.testimonials.length]);

  const [openIndex, setOpenIndex] = useState(null);

  const handleClick = (index) => {
    setOpenIndex(openIndex === index ? null : index);
  };

  return (
    <div className="container mx-auto p-6 md:p-10 lg:p-14">
      {/* Header Section */}
      <section className="text-center py-20 bg-gradient-to-r from-blue-500 to-indigo-600 rounded-lg shadow-lg text-white">
        <h1 className="text-4xl md:text-5xl font-extrabold mb-6">
          Premium Website Design and Development Services
        </h1>
        <p className="text-pretty md:text-xl mb-8">
          Is your website confusing visitors instead of converting them? With
          the right mix of user experience and stunning design, you’ll ignite
          growth. Let us take you there.
        </p>
        <button className="bg-yellow-400 text-black font-bold py-3 px-6 rounded-full hover:bg-yellow-500 transition-all transform hover:scale-105">
          Build Your Website Now
        </button>
      </section>

      {/* Error Handling */}
      {error && (
        <div className="bg-red-200 text-red-600 p-4 rounded-lg text-center mt-4">
          {error}
        </div>
      )}

      {/* Services Section */}
      <div className="mt-20">
        <h2 className="text-3xl font-bold mb-6 text-center">Our Services</h2>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
          {Array.isArray(data.services) && data.services.length > 0 ? (
            data.services.map((service) => (
              <div key={service.id} className="border rounded-lg overflow-hidden shadow-lg hover:shadow-xl transition-shadow">
                <img src={service.image} alt={service.alt} className="w-full h-40 object-cover transition-transform transform hover:scale-105" />
                <div className="p-4 bg-white">
                  <h3 className="text-xl font-bold mb-2">{service.title}</h3>
                  <p className="text-gray-600">{ service.description}</p>
                </div>
              </div>
            ))
          ) : (
            <p className="text-gray-600 text-center">No services available.</p>
          )}
        </div>
      </div>

      {/* Testimonials Section */}
      <div className="mt-20">
        <h2 className="text-3xl font-bold mb-6 text-center">What Our Clients Say</h2>
        <div className="flex justify-center">
          <div className="w-full max-w-md">
            {Array.isArray(data.testimonials) && data.testimonials.length > 0 ? (
              <div className="relative">
                <div className="border rounded-lg p-4 mb-4 transition-opacity duration-500">
                  <img src={data.testimonials[currentTestimonialIndex]?.image} alt={data.testimonials[currentTestimonialIndex]?.name} className="w-12 h-12 rounded-full mb-2" />
                  <p className="text-gray-700">{data.testimonials[currentTestimonialIndex]?.text}</p>
                  <h4 className="font-bold">{data.testimonials[currentTestimonialIndex]?.name}</h4>
                  <p className="text-sm text-gray-500">{data.testimonials[currentTestimonialIndex]?.title}</p>
                </div>
                <div className="absolute top-0 left-0 right-0 flex justify-between mt-2">
                  <button className="bg-gray-300 p-2 rounded-full hover:bg-gray-400 transition" onClick={() => setCurrentTestimonialIndex((currentTestimonialIndex - 1 + data.testimonials.length) % data.testimonials.length)}>
                    <i className="fas fa-chevron-left"></i>
                  </button>
                  <button className="bg-gray-300 p-2 rounded-full hover:bg-gray-400 transition" onClick={() => setCurrentTestimonialIndex((currentTestimonialIndex + 1) % data.testimonials.length)}>
                    <i className="fas fa-chevron-right"></i>
                  </button>
                </div>
              </div>
            ) : (
              <p className="text-gray-600 text-center">No testimonials available.</p>
            )}
          </div>
        </div>
      </div>

      {/* FAQ Section */}
      <div className="mt-20">
        <h2 className="text-3xl font-bold mb-6 text-center">Frequently Asked Questions</h2>
        <div className="w-full max-w-2xl mx-auto">
          {Array.isArray(data.faqs) && data.faqs.length > 0 ? (
            data.faqs.map((faq, index) => (
              <div key={faq.id} className="border-b border-gray-300 mb-2">
                <div
                  className="flex justify-between items-center py-4 cursor-pointer"
                  onClick={() => handleClick(index)}
                >
                  <span className="text-lg">{faq.question}</span>
                  <i className={`fas fa-chevron-${openIndex === index ? "up" : "down"}`}></i>
                </div>
                {openIndex === index && (
                  <div className="py-2 text-gray-700">{faq.answer}</div>
                )}
              </div>
            ))
          ) : (
            <p className="text-gray-600 text-center">No FAQs available.</p>
          )}
        </div>
      </div>
    </div>
  );
}